import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft2 } from 'iconsax-react';
import { formatToNaira } from 'functions/currencyConveter';
import { RoutePaths } from 'routes/route-paths';
import { FiCreditCard } from 'react-icons/fi';
import SavingsTabs from 'components/tabs/SavingsTabs';
import { appAxios } from 'api/axios';
import { getTokenDetails } from 'functions/userSession';
import { decodeToken } from 'react-jwt';
import { TokenType } from 'types/jwt';
import { ChartData } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Loader } from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { savingsSlice, selectInterestRate } from 'store/slices/savings';
import StartSavingModal from 'components/shared/modal/StartSavingsModal';

// Register required components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface SavingsHistoryResponse {
  success: boolean;
  message: string;
  data: {
    month: number;
    year: number;
    totalSavings: number;
    dailySavings: Array<{
      date: string;
      amount: number;
    }>;
  };
  statusCode: number;
}

function HowJomptVaultWorks() {
  const token = getTokenDetails();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [interestRate, setInterestRate] = useState<number>(0);
  const decodedToken = decodeToken<TokenType>(token!);
  const customerId = decodedToken?.customerId;

  const dispatch = useAppDispatch();
  const storeInterestRate = useAppSelector(selectInterestRate);

  const [allSavings, setAllSavings] = useState<[]>([]);
  const [totalSavingsBalance, setTotalSavingsBalance] = useState();
  const [accruedInterest, setAccruedInterest] = useState<number>(0);
  const [activeTab, setActiveTab] = useState('ongoing');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [savingsHistory, setSavingsHistory] = useState<
    SavingsHistoryResponse['data']
  >({
    month: 0,
    year: 0,
    totalSavings: 0,
    dailySavings: [],
  });

  const handleGoBack = () => {
    navigate(-1); // Go back to the previous route
  };

  const goToCreateSavings = () => {
    navigate(RoutePaths.USER_CREATE_SAVINGS_JOMPVAULT);
  };

  // Prepare data for the chart
  const chartData: ChartData<'bar'> = {
    labels: savingsHistory.dailySavings.map((item) => item.date),
    datasets: [
      {
        label: 'Amount Saved',
        data: savingsHistory.dailySavings.map((item) => item.amount),
        backgroundColor: '#31005C',
        borderColor: '#31005C',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: '',
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
      },
    },
  };

  useEffect(() => {
    const getAllSavings = async () => {
      try {
        setLoading(true);
        const { data } = await appAxios.get(
          `get-customer-savings?CustomerId=${customerId}`
        );
        if (data && data.statusCode === 200) {
          setAllSavings(data.data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    getAllSavings();
  }, [activeTab, activeTab === 'ongoing']);

  useEffect(() => {
    const getTotalSavingsBalance = async () => {
      try {
        setLoading(true);
        const { data } = await appAxios.get(
          `get-total-savings-balance?customerId=${customerId}`
        );
        if (data && data.statusCode === 200) {
          console.log(data, 'data');
          setTotalSavingsBalance(data.data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    getTotalSavingsBalance();
  }, []);

  useEffect(() => {
    const getSavingsHistory = async () => {
      try {
        const { data } = await appAxios.get<SavingsHistoryResponse>(
          `get-savings-history?CustomerId=${customerId}`
        );
        if (data.success) {
          setSavingsHistory(data.data);
        }
      } catch (error) {
        console.error('Error fetching savings history:', error);
      }
    };

    getSavingsHistory();
  }, [customerId]);

  useEffect(() => {
    const fetchInterestRate = async () => {
      try {
        // Check if we already have the interest rate in the Redux store
        if (storeInterestRate > 0) {
          setInterestRate(storeInterestRate);
          return;
        }

        // If not in store, fetch from API
        const { data } = await appAxios.get('/get-savings-type');
        if (data && data.success) {
          const jompVaultData = data.data.find(
            (item: any) => item.name === 'jompVault'
          );
          if (jompVaultData) {
            const rate = jompVaultData.interestRate;
            setInterestRate(rate);
            dispatch(savingsSlice.actions.setInterestRate(rate));
          }
        }
      } catch (error) {
        console.error('Error fetching interest rate:', error);
      }
    };

    fetchInterestRate();
  }, [dispatch, storeInterestRate]);

  useEffect(() => {
    const getAccruedInterest = async () => {
      try {
        const { data } = await appAxios.get(
          `get-accrued-interest?customerId=${customerId}`
        );
        if (data && data.statusCode === 200) {
          setAccruedInterest(data.data);
        }
      } catch (error) {
        console.error('Error fetching accrued interest:', error);
        setAccruedInterest(0);
      }
    };

    getAccruedInterest();
  }, [customerId]);

  return (
    <div className="px-4 p-6 md:p-6  bg-[#F8F9FC] min-h-screen">
      <div className="flex flex-col gap-y-4">
        {/* Back Button */}
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 text-[#EFA005]"
        >
          <ArrowLeft2 />
          Back
        </button>

        <div className="flex flex-col justify-between md:items-center md:flex-row lg:flex-row">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-2xl font-semibold">Savings</h1>
            <p>
              Reach your unique individual saving goals with {interestRate}%
              interest P.A
            </p>
          </div>

          <button
            className="bg-[#EFA005] text-white rounded-md  px-5 py-4"
            onClick={() => setIsModalOpen(true)}
          >
            Create Savings
          </button>
        </div>
      </div>

      {/* Header */}

      {/* Cards */}
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="flex flex-col gap-3">
          <div className="p-6 h-1/2 text-white bg-gradient-to-r from-purple-400 to-indigo-600 rounded-lg">
            <h3 className="text-lg font-medium">Total Savings Balance</h3>
            {loading ? (
              <Loader className="animate-spin" />
            ) : (
              <p className="text-2xl font-semibold">
                {formatToNaira(totalSavingsBalance) || 0}
              </p>
            )}
          </div>
          <div className="bg-[#EFA005] h-1/2 p-6 rounded-lg text-white">
            <p className="text-sm">
              This is all you have earned based on all your savings. To earn
              more, keep saving!
            </p>
            <p className="text-2xl font-semibold">
              {formatToNaira(accruedInterest)}
            </p>
          </div>
        </div>
        {/* Savings History */}
        <div className="px-6 py-3 bg-white rounded-lg shadow-md">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Savings History</h3>
            <button className="px-3 py-1 text-gray-600 rounded-md border">
              This Month
            </button>
          </div>

          <Bar data={chartData} options={chartOptions} />
          {/* </div> */}
        </div>
      </div>

      {/* Transactions */}
      <div className="p-6 mt-6 bg-white rounded-lg shadow-md">
        <div className="flex justify-between items-center">
          {/* Icon + Transactions Text */}
          <div className="flex gap-3 items-center">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#595BD426]">
              <FiCreditCard className="text-lg text-gray-600" /> {/* Icon */}
            </div>
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold">Transactions</h3>
              <span className="text-xs text-gray-500">
                View all transactions
              </span>
            </div>
          </div>

          <Link
            to={`${RoutePaths.USER_SAVINGS_TRANSACTION}`}
            className="text-[#EFA005] hover:underline"
          >
            View
          </Link>
        </div>
      </div>

      <SavingsTabs
        allSavings={allSavings}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <StartSavingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title=""
        subtitle=""
        btnText="Proceed"
        onAction={goToCreateSavings}
      />
    </div>
  );
}

export default HowJomptVaultWorks;
